import type { KyInstance } from 'ky';

import { ensureError, MINUTE } from '@enterprise/core';
import type { FileUpload } from '@enterprise/types';

import { Uri } from '#constants/uri';

/**
 * Uploads one or more mailing suppression files to the server.
 *
 * This function constructs a `FormData` payload containing the files and their associated metadata,
 * then sends a POST request to the suppressions endpoint using the provided `KyInstance`.
 * Each file's metadata is augmented with its file size before being stringified and appended to the form data.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param fileUploads - An array of file uploads, each containing a `file` and its associated `metaData`.
 * @returns A promise that resolves with the server's JSON response.
 * @throws Will throw an error if the upload fails.
 */
export const uploadMailingSuppressions = async (instance: KyInstance, fileUploads: FileUpload[]) => {
  try {
    const formData = new FormData();

    for (const { file, metaData } of fileUploads) {
      formData.append('metaData', JSON.stringify({ ...metaData, fileSize: file.size }));
      formData.append('file', file);
    }

    return await instance
      .extend({
        retry: 0,
        timeout: 5 * MINUTE,
      })
      .post(Uri.files.suppressions, { body: formData })
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
